"use client";

import React from "react";
import { Loader, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const inputClass = "bg-transparent border-0 border-b rounded-none px-0 py-2 text-base font-default focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:opacity-40 transition-colors duration-200";
  const inputStyle = {
    borderBottomColor: "rgba(244,244,249,0.30)",
    color: "var(--foreground)",
    outline: "none",
    boxShadow: "none"
  };
  if (isSubmitted && !submitError) {
    return <div data-section-id="231704" className="flex flex-col items-center justify-center py-16 gap-6" style={{
      minHeight: 320
    }}>
        <div className="size-16 rounded-full flex items-center justify-center" style={{
        background: "var(--primary)"
      }}>
          <Mail className="size-8" style={{
          color: "var(--primary-foreground)"
        }} />
        </div>
        <p className="text-lg font-default text-center" style={{
        color: "var(--foreground)"
      }}>
          {successMessage}
        </p>
        <Button onClick={resetForm} className="font-default font-bold uppercase tracking-wider text-sm rounded-full" style={{
        background: "var(--primary)",
        color: "var(--primary-foreground)",
        border: "none"
      }}>
          Weitere Anfrage senden
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="full_name" rules={{
      required: "Vollständiger Name ist erforderlich",
      minLength: {
        value: 2,
        message: "Mind. 2 Zeichen"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-widest font-mono" style={{
        color: "rgba(244,244,249,0.55)"
      }}>
              Vollständiger Name
            </FormLabel>
            <FormControl>
              <Input type="text" placeholder="Max Mustermann" className={inputClass} style={inputStyle} {...field} />
            </FormControl>
            <FormDescription className="text-xs font-sans" style={{
        color: "rgba(244,244,249,0.35)"
      }}>
              Geben Sie Ihren Vor- und Nachnamen ein.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "E-Mail-Adresse ist erforderlich",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Ungültige E-Mail-Adresse"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-widest font-mono" style={{
        color: "rgba(244,244,249,0.55)"
      }}>
              E-Mail Adresse
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="max@beispiel.de" className={inputClass} style={inputStyle} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+\d\s\-().]{7,20}$/,
          message: "Ungültige Telefonnummer"
        }
      }} render={({
        field
      }) => <FormItem className="gap-1">
              <FormLabel className="text-xs uppercase tracking-widest font-mono" style={{
          color: "rgba(244,244,249,0.55)"
        }}>
                Telefonnummer
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+49 (0) 123 456789" className={inputClass} style={inputStyle} {...field} />
              </FormControl>
              <FormDescription className="text-xs font-sans" style={{
          color: "rgba(244,244,249,0.35)"
        }}>
                Für eine schnellere Terminabsprache.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem className="gap-1">
              <FormLabel className="text-xs uppercase tracking-widest font-mono" style={{
          color: "rgba(244,244,249,0.55)"
        }}>
                Gewünschte Leistung
              </FormLabel>
              <FormControl>
                <Input type="text" placeholder="z.B. Baumfällung" className={inputClass} style={inputStyle} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Nachricht ist erforderlich",
      minLength: {
        value: 20,
        message: "Nachricht muss mindestens 20 Zeichen lang sein"
      },
      maxLength: {
        value: 2000,
        message: "Nachricht darf maximal 2000 Zeichen lang sein"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-widest font-mono" style={{
        color: "rgba(244,244,249,0.55)"
      }}>
              Ihre Situation
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Beschreiben Sie kurz Ihr Anliegen..." className={`${inputClass} min-h-28 resize-none font-sans`} style={inputStyle} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-none border" style={{
      borderColor: "var(--destructive)",
      background: "rgba(200,50,50,0.1)"
    }}>
          <p className="text-sm font-sans" style={{
        color: "var(--destructive)"
      }}>
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full font-default font-bold uppercase tracking-widest text-sm rounded-full h-12 mt-2 transition-transform hover:scale-[1.02] bg-primary text-primary-foreground">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Wird gesendet...
          </> : "Kostenlose Einschätzung sichern"}
      </Button>
    </Form>;
}
const navLinks = [{
  label: "Startseite",
  to: "/HomePage"
}, {
  label: "Fällung & Wurzeln",
  to: "/FellingAndRootsPage"
}, {
  label: "Pflege & Freischnitt",
  to: "/CareAndPruningPage"
}, {
  label: "Bau & Begleitung",
  to: "/BuildingAndSupportPage"
}, {
  label: "Kontakt",
  to: "#kostenlose-einschätzung-sichern"
}];
export default function Contact() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: detailsRef,
    isVisible: detailsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}